"use client"

// Dibujo de la pizza en construcción.
//
// Vive aparte del armador porque lo van a compartir el microsite, el kiosko y el POS,
// y porque su única responsabilidad es dibujar: no conoce precios ni reductor.
//
// 🔴 TODO ES DETERMINISTA. La semilla sale del id del ingrediente, así que el mismo
// topping cae siempre en las mismas posiciones, con la misma rotación y el mismo
// tamaño. Sin eso la pizza "hierve" en cada render y se ve rota.
//
// Tres decisiones que separan esto de un clip-art:
//
//  1. LA MASA NO ES UN CÍRCULO. Una pizza real no es perfecta: el borde se estira a
//     mano. El contorno se genera con ruido suave sobre el radio y se cierra con
//     curvas Catmull-Rom, no con `<circle>`. Es lo que más aporta a que se lea como
//     comida y no como ícono.
//  2. EL QUESO SON CHARCOS, no una capa plana. Manchas irregulares superpuestas con
//     variación de tono, más puntos dorados de gratinado.
//  3. CADA INGREDIENTE TIENE SU FORMA. El champiñón es un sombrero, la aceituna un
//     anillo, la cebolla una tira curva, el pepperoni un disco con borde tostado.
//     Pintarlos todos como el mismo círculo de color es lo que delata un dibujo
//     genérico.

import { useMemo } from "react"
import type { Zone, ZoneLayout } from "@/lib/menu/zonedPricing"
import { presetDe, esCapaDeQueso, type Forma } from "@/lib/menu/pizzaIngredients"
import { BORDE, COLOR_SALSA, type Masa, type NivelQueso, type Salsa } from "@/lib/menu/pizzaBase"
import { RADIO_SALSA, fotoBase, fotoTopping, tamanoFoto } from "@/lib/menu/pizzaFotos"

export const PIZZA_R = 118
export const PIZZA_CX = 160

export type PizzaTopping = {
  optionId: string
  /** Nombre del ingrediente: decide la FORMA con la que se dibuja. */
  name: string
  color: string
  zone: Zone
  /** 2 = doble ración ⇒ el doble de piezas en esa zona. */
  quantity: number
}

export interface PizzaCanvasProps {
  toppings: readonly PizzaTopping[]
  layout: ZoneLayout
  /** Prefijo de los clipPath. Dos pizzas en la misma página no pueden compartir ids. */
  idPrefix: string
  className?: string
  /** Para posicionarla fuera de flujo (el kiosko la monta sobre la esquina). */
  style?: React.CSSProperties
  title?: string
  /** Salsa por zona. Vacío ⇒ roja en toda la pizza (lo de siempre). */
  salsas?: ReadonlyArray<{ zone: Zone; tipo: Salsa }>
  /** Grosor del borde. Ausente ⇒ delgada. */
  masa?: Masa
  /** `sin` quita la capa de queso; `doble` la hace más densa. */
  queso?: NivelQueso
  /** Cuánto ocupa la pizza dentro del recuadro (0..1). Es lo que hace que una
   *  familiar se vea más grande que una mediana. */
  escala?: number
  /** Modo FOTOGRÁFICO (por defecto): base e ingredientes son fotos reales de
   *  public/pizza-fotos, compuestas con los mismos clips de zona y el mismo
   *  sembrado determinista del modo vectorial. `false` fuerza el dibujo — y un
   *  ingrediente SIN foto cae solo al dibujo vectorial, nunca a un hueco. */
  fotos?: boolean
  /** Los ingredientes CAEN al aparecer. Para la pantalla del cliente, donde la
   *  construcción es el espectáculo. En el armador del cajero va apagado: ahí la
   *  pizza es una referencia que se consulta, y algo que salta en cada toque
   *  estorba a quien está tecleando contra el reloj. */
  animate?: boolean
}

/**
 * Redondeo a 2 decimales para TODO número que termine en un atributo del SVG.
 *
 * 🔴 NO es cosmético: sin esto el servidor y el cliente serializan el mismo flotante
 * con distinto último dígito (`cx="75.86556191230935"` vs `75.86556191230936`) y
 * React reporta desajuste de hidratación. Cuando eso pasa React NO parchea el árbol:
 * se queda con el HTML del servidor, así que la pizza podría quedar congelada en el
 * estado inicial. En el microsite no se notaba porque el modal se abre por clic y
 * nunca se renderiza en el servidor; en la pantalla del cliente sí se renderiza.
 *
 * Dos decimales sobre un lienzo de 320 unidades es una diezmilésima del ancho: por
 * debajo de un píxel en cualquier pantalla.
 */
const q = (n: number) => Math.round(n * 100) / 100

// ── Azar determinista ────────────────────────────────────────────────────────
function hash32(s: string): number {
  let h = 2166136261
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) }
  return h >>> 0
}
function rng(seed: string) {
  let s = hash32(seed)
  return () => {
    s = (s + 0x6d2b79f5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Contorno irregular cerrado con curvas suaves (Catmull-Rom → Bézier). Un
 *  `<circle>` se lee como ícono; esto se lee como masa estirada a mano. */
function blobPath(cx: number, cy: number, radio: number, irregularidad: number, seed: string, puntos = 18): string {
  const r = rng(seed)
  const pts: Array<[number, number]> = []
  for (let i = 0; i < puntos; i++) {
    const a = (i / puntos) * Math.PI * 2
    const rr = radio * (1 + (r() - 0.5) * irregularidad)
    pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr])
  }
  let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`
  for (let i = 0; i < puntos; i++) {
    const p0 = pts[(i - 1 + puntos) % puntos]
    const p1 = pts[i]
    const p2 = pts[(i + 1) % puntos]
    const p3 = pts[(i + 2) % puntos]
    const c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6
    const c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`
  }
  return d + " Z"
}

/**
 * Rango angular de cada zona, en radianes.
 *
 * 🔴 LAS PIEZAS SE GENERAN DENTRO DE SU ZONA, no por toda la pizza para después
 * recortarlas. Dispersar y recortar deja visible solo lo que por azar cayó del lado
 * correcto: "pepperoni a la izquierda" mostraba 6 piezas de 13, apelotonadas. El
 * clipPath queda igual, como red de seguridad para las que rozan el borde.
 *
 * En SVG la Y crece hacia abajo, así que sin(a) > 0 es la mitad INFERIOR.
 */
const RANGO: Record<Zone, [number, number]> = {
  whole: [0, Math.PI * 2],
  left:  [Math.PI / 2, Math.PI * 1.5],
  right: [-Math.PI / 2, Math.PI / 2],
  q1:    [Math.PI, Math.PI * 1.5],
  q2:    [Math.PI * 1.5, Math.PI * 2],
  q3:    [0, Math.PI / 2],
  q4:    [Math.PI / 2, Math.PI],
}

// ── Formas por ingrediente ───────────────────────────────────────────────────
// La forma Y EL COLOR salen del catálogo (lib/menu/pizzaIngredients), no de un hash
// del nombre. Ese hash es lo que hacía que el pepperoni saliera beige.

/** El nombre decide la forma. Se normaliza sin tildes para que "Champiñones" y
 *  "champinon" caigan igual — el dueño escribe el menú a mano. */
function pieza(forma: Forma, color: string, s: number, key: string) {
  const oscuro = mezclar(color, "#3A1F14", 0.38)
  const muyOscuro = mezclar(color, "#2A1108", 0.55)
  const claro = mezclar(color, "#FFFFFF", 0.34)
  const luz = "#FFFFFF"
  switch (forma) {
    case "hongo":
      // Sombrero + laminillas + tallo. Las laminillas son lo que lo hace reconocible
      // al tamaño diminuto en que se dibuja.
      return (
        <g key={key}>
          <path d={`M ${-s * 1.05} ${s * 0.05} A ${s * 1.05} ${s * 0.92} 0 0 1 ${s * 1.05} ${s * 0.05} Z`} fill={oscuro} />
          <path d={`M ${-s * 0.95} 0 A ${s * 0.95} ${s * 0.82} 0 0 1 ${s * 0.95} 0 Z`} fill={color} />
          <path d={`M ${-s * 0.5} ${-s * 0.42} A ${s * 0.5} ${s * 0.4} 0 0 1 ${s * 0.1} ${-s * 0.5}`}
                fill="none" stroke={luz} strokeWidth={s * 0.16} strokeLinecap="round" opacity={0.45} />
          <rect x={-s * 0.3} y={-0.5} width={s * 0.6} height={s * 0.66} rx={s * 0.2} fill={claro} />
          <line x1={-s * 0.55} y1={s * 0.02} x2={s * 0.55} y2={s * 0.02} stroke={muyOscuro} strokeWidth={0.5} opacity={0.5} />
        </g>
      )
    case "anillo":
      // Aro de verdad: el centro se ve el queso a través, no un punto negro pintado.
      return (
        <g key={key}>
          <circle r={s} fill={oscuro} />
          <circle r={s * 0.9} fill={color} />
          <circle r={s * 0.38} fill={muyOscuro} opacity={0.85} />
          <path d={`M ${-s * 0.62} ${-s * 0.42} A ${s * 0.75} ${s * 0.75} 0 0 1 ${s * 0.1} ${-s * 0.74}`}
                fill="none" stroke={luz} strokeWidth={s * 0.18} strokeLinecap="round" opacity={0.4} />
        </g>
      )
    case "tira":
      return (
        <g key={key}>
          <rect x={-s * 1.55} y={-s * 0.38} width={s * 3.1} height={s * 0.76} rx={s * 0.38} fill={oscuro} />
          <rect x={-s * 1.45} y={-s * 0.3} width={s * 2.9} height={s * 0.6} rx={s * 0.3} fill={color} />
          <rect x={-s * 1.15} y={-s * 0.24} width={s * 1.6} height={s * 0.18} rx={s * 0.09} fill={luz} opacity={0.35} />
        </g>
      )
    case "cuna":
      return (
        <g key={key}>
          <path d={`M 0 ${-s * 1.05} L ${s * 0.95} ${s * 0.75} L ${-s * 0.95} ${s * 0.75} Z`} fill={oscuro} />
          <path d={`M 0 ${-s * 0.85} L ${s * 0.78} ${s * 0.62} L ${-s * 0.78} ${s * 0.62} Z`} fill={color} />
          <path d={`M 0 ${-s * 0.5} L ${s * 0.3} ${s * 0.32} L ${-s * 0.3} ${s * 0.32} Z`} fill={claro} opacity={0.6} />
        </g>
      )
    case "jiron":
      // Jamón: borde irregular y translúcido, como una lonja que se dobló.
      return (
        <g key={key} opacity={0.96}>
          <path d={`M ${-s} ${-s * 0.72} Q ${-s * 0.1} ${-s * 1.05}, ${s * 0.85} ${-s * 0.82} Q ${s * 1.1} ${s * 0.1}, ${s * 0.6} ${s * 0.7} Q ${-s * 0.2} ${s * 1.05}, ${-s * 0.85} ${s * 0.55} Z`} fill={oscuro} />
          <path d={`M ${-s * 0.86} ${-s * 0.6} Q ${-s * 0.08} ${-s * 0.88}, ${s * 0.72} ${-s * 0.68} Q ${s * 0.94} ${s * 0.08}, ${s * 0.5} ${s * 0.58} Q ${-s * 0.18} ${s * 0.88}, ${-s * 0.72} ${s * 0.45} Z`} fill={color} />
          <path d={`M ${-s * 0.5} ${-s * 0.45} Q ${0} ${-s * 0.6}, ${s * 0.35} ${-s * 0.4}`}
                fill="none" stroke={luz} strokeWidth={s * 0.18} strokeLinecap="round" opacity={0.4} />
        </g>
      )
    case "rizo":
      // Tocineta: la tira ondulada + una veta de grasa más clara encima.
      return (
        <g key={key}>
          {/* Onda BAJA y trazo ancho: con más amplitud la tira se enrosca y se lee
              como un gusano, no como una lonja de tocineta. */}
          <path d={`M ${-s * 1.7} 0 Q ${-s * 0.6} ${-s * 0.42}, ${s * 0.1} 0 T ${s * 1.7} ${s * 0.06}`}
                fill="none" stroke={muyOscuro} strokeWidth={s * 0.72} strokeLinecap="round" />
          <path d={`M ${-s * 1.7} 0 Q ${-s * 0.6} ${-s * 0.42}, ${s * 0.1} 0 T ${s * 1.7} ${s * 0.06}`}
                fill="none" stroke={color} strokeWidth={s * 0.54} strokeLinecap="round" />
          {/* La veta de grasa: es lo que la vuelve reconocible como tocineta. */}
          <path d={`M ${-s * 1.55} ${-s * 0.1} Q ${-s * 0.55} ${-s * 0.5}, ${s * 0.1} ${-s * 0.1}`}
                fill="none" stroke={claro} strokeWidth={s * 0.16} strokeLinecap="round" opacity={0.8} />
        </g>
      )
    case "hoja":
      // Albahaca / rúcula: hoja con nervadura. Sin la nervadura se lee como una
      // mancha verde; con ella, como una hoja, aun a 5 px.
      return (
        <g key={key}>
          <path d={`M 0 ${-s * 1.3} Q ${s * 0.95} ${-s * 0.2}, 0 ${s * 1.3} Q ${-s * 0.95} ${-s * 0.2}, 0 ${-s * 1.3} Z`} fill={oscuro} />
          <path d={`M 0 ${-s * 1.15} Q ${s * 0.8} ${-s * 0.18}, 0 ${s * 1.15} Q ${-s * 0.8} ${-s * 0.18}, 0 ${-s * 1.15} Z`} fill={color} />
          <line x1={0} y1={-s * 1.05} x2={0} y2={s * 1.05} stroke={claro} strokeWidth={s * 0.13} opacity={0.65} />
          <path d={`M ${-s * 0.3} ${-s * 0.5} Q ${-s * 0.55} ${-s * 0.75}, ${-s * 0.15} ${-s * 0.95}`}
                fill="none" stroke={luz} strokeWidth={s * 0.1} opacity={0.35} />
        </g>
      )
    case "escama":
      // Orégano / parmesano: hojuelas irregulares y planas. Van muchas y chicas.
      return (
        <g key={key}>
          <path d={`M ${-s} ${-s * 0.4} L ${s * 0.7} ${-s * 0.75} L ${s} ${s * 0.35} L ${-s * 0.5} ${s * 0.8} Z`} fill={color} />
          <path d={`M ${-s * 0.5} ${-s * 0.3} L ${s * 0.3} ${-s * 0.5}`} stroke={claro} strokeWidth={s * 0.18} strokeLinecap="round" opacity={0.55} />
        </g>
      )
    case "huevo":
      // Huevo: clara irregular + yema con su brillo. Va grande y en pocas unidades.
      return (
        <g key={key}>
          <path d={`M ${-s} ${-s * 0.55} Q ${-s * 0.15} ${-s * 1.15}, ${s * 0.8} ${-s * 0.6} Q ${s * 1.15} ${s * 0.3}, ${s * 0.35} ${s * 0.9} Q ${-s * 0.6} ${s * 1.1}, ${-s} ${-s * 0.55} Z`} fill="#FCF6E9" />
          <circle cx={s * 0.05} cy={-s * 0.05} r={s * 0.46} fill="#E8A33D" />
          <circle cx={-s * 0.12} cy={-s * 0.2} r={s * 0.18} fill="#FFD98A" opacity={0.75} />
        </g>
      )
    case "grano":
      return (
        <g key={key}>
          <ellipse rx={s} ry={s * 0.78} fill={oscuro} />
          <ellipse rx={s * 0.88} ry={s * 0.66} fill={color} />
          <ellipse cx={-s * 0.24} cy={-s * 0.2} rx={s * 0.34} ry={s * 0.22} fill={luz} opacity={0.34} />
        </g>
      )
    default:
      // Pepperoni: se ACOPA al hornear — borde levantado y oscuro, centro más claro,
      // motas de grasa. Es el ingrediente que más se mira, así que lleva más detalle.
      return (
        <g key={key}>
          <circle r={s * 1.04} fill={muyOscuro} opacity={0.85} />
          <circle r={s * 0.96} fill={oscuro} />
          <circle r={s * 0.8} fill={color} />
          <circle cx={-s * 0.2} cy={-s * 0.22} r={s * 0.34} fill={claro} opacity={0.5} />
          <circle cx={s * 0.26} cy={s * 0.18} r={s * 0.13} fill={muyOscuro} opacity={0.6} />
          <circle cx={-s * 0.3} cy={s * 0.3} r={s * 0.1} fill={muyOscuro} opacity={0.5} />
          <circle cx={-s * 0.34} cy={-s * 0.36} r={s * 0.16} fill={luz} opacity={0.4} />
        </g>
      )
  }
}

/** Mezcla dos hex. Se hace acá y no con `color-mix` de CSS porque los atributos SVG
 *  `fill`/`stroke` no lo aceptan en todos los motores. */
function mezclar(a: string, b: string, t: number): string {
  const p = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16))
  const [r1, g1, b1] = p(a), [r2, g2, b2] = p(b)
  const m = (x: number, y: number) => Math.round(x + (y - x) * t).toString(16).padStart(2, "0")
  return `#${m(r1, r2)}${m(g1, g2)}${m(b1, b2)}`
}

/**
 * Un ingrediente suelto, con LA MISMA forma que va a tener sobre la pizza.
 *
 * Es para los chips del selector: un champiñón se ve como champiñón y una aceituna
 * como aceituna. Reusar `pieza` en vez de dibujar un ícono aparte garantiza que el
 * chip y la pizza nunca se contradigan — si mañana cambia la forma, cambian las dos.
 */
export function IngredientGlyph({ name, color, size = 22 }: { name: string; color: string; size?: number }) {
  const s = size * 0.34
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" className="flex-none">
      <g transform={`translate(${size / 2} ${size / 2})`}>
        {(() => {
          // Mismo catálogo que la pizza: el chip y la pieza del plato no pueden
          // mostrar un pepperoni de distinto color.
          const p = presetDe(name)
          return pieza(p?.forma ?? "disco", p?.color ?? color, s, "glyph")
        })()}
      </g>
    </svg>
  )
}

export function PizzaCanvas({
  toppings, layout, idPrefix, className, style, title, animate = false,
  salsas, masa = "delgada", queso, escala = 1, fotos = true,
}: PizzaCanvasProps) {
  const CX = PIZZA_CX, R = PIZZA_R
  const id = (n: string) => `${idPrefix}-${n}`
  const CLIP: Record<Zone, string> = {
    whole: id("whole"), left: id("left"), right: id("right"),
    q1: id("q1"), q2: id("q2"), q3: id("q3"), q4: id("q4"),
  }

  // La masa y el queso no dependen de los toppings: se calculan una vez.
  // El borde crece con el tipo de masa: es la diferencia que el cliente comprueba de
  // un vistazo entre una delgada y una rellena.
  const borde = BORDE[masa]
  const dMasa = useMemo(() => blobPath(CX, CX, R + borde, 0.045, `${idPrefix}-masa`, 22), [CX, R, borde, idPrefix])
  const dSalsa = useMemo(() => blobPath(CX, CX, R - 2, 0.06, `${idPrefix}-salsa`, 20), [CX, R, idPrefix])
  /** El MANTO de queso: más chico que la salsa a propósito, para que el rojo asome
   *  como un anillo junto a la corteza — es como se ve una pizza real, donde el
   *  queso fundido no llega hasta el borde. */
  const dQueso = useMemo(() => blobPath(CX, CX, R - 11, 0.055, `${idPrefix}-queso-base`, 18), [CX, R, idPrefix])

  /** Manchas tostadas del borde: es lo que hace que una masa parezca horneada. */
  const char = useMemo(() => {
    const r = rng(`${idPrefix}-char`)
    return Array.from({ length: 26 }, () => {
      const a = r() * Math.PI * 2
      const rad = R + 4 + r() * 11
      return { x: q(CX + Math.cos(a) * rad), y: q(CX + Math.sin(a) * rad), rx: q(2 + r() * 4), ry: q(1.4 + r() * 2.6), rot: q(r() * 180), op: q(0.16 + r() * 0.3) }
    })
  }, [CX, R, idPrefix])

  /**
   * Queso: MUCHOS parches chicos que se solapan, no una mancha grande.
   *
   * La versión anterior pintaba 9 charcos enormes y el resultado se leía como una
   * mancha pálida en el centro, no como queso. La mozzarella derretida es una
   * superficie irregular hecha de bordes que se tocan: se consigue con parches
   * pequeños, translúcidos y de amarillos ligeramente distintos.
   */
  const parches = useMemo(() => {
    const r = rng(`${idPrefix}-parches`)
    const AMARILLOS = ["#F8E0A0", "#F3C963", "#EDB44A", "#FAD98C"]
    return Array.from({ length: 30 }, (_, i) => {
      const a = r() * Math.PI * 2
      const rad = Math.sqrt(r()) * (R - 14)
      return {
        d: blobPath(q(CX + Math.cos(a) * rad), q(CX + Math.sin(a) * rad), q(9 + r() * 15), 0.46, `${idPrefix}-parche-${i}`, 9),
        fill: AMARILLOS[Math.floor(r() * AMARILLOS.length)],
        op: q(0.4 + r() * 0.35),
      }
    })
  }, [CX, R, idPrefix])

  /**
   * Manchas de horneado ("leopardo"). Es LO QUE HACE QUE SE VEA HORNEADA: una
   * mozzarella que salió del horno no es amarilla pareja, tiene zonas tostadas
   * irregulares. Sin esto la pizza parece cruda por más textura que se le ponga.
   */
  const leopardo = useMemo(() => {
    const r = rng(`${idPrefix}-leopardo`)
    const TOSTADOS = ["#C77B2E", "#B4661F", "#9E5518", "#D68F3C"]
    return Array.from({ length: 34 }, (_, i) => {
      const a = r() * Math.PI * 2
      const rad = Math.sqrt(r()) * (R - 18)
      return {
        d: blobPath(q(CX + Math.cos(a) * rad), q(CX + Math.sin(a) * rad), q(4 + r() * 9), 0.55, `${idPrefix}-leo-${i}`, 8),
        fill: TOSTADOS[Math.floor(r() * TOSTADOS.length)],
        op: q(0.38 + r() * 0.42),
      }
    })
  }, [CX, R, idPrefix])

  /** Burbujas del queso: cada una con su brillo arriba-izquierda. Son lo que da
   *  la sensación de RELIEVE — sin ellas la superficie sigue siendo plana. */
  const burbujas = useMemo(() => {
    const r = rng(`${idPrefix}-burbujas`)
    return Array.from({ length: 18 }, () => {
      const a = r() * Math.PI * 2
      const rad = Math.sqrt(r()) * (R - 28)
      const s = q(2.2 + r() * 3.4)
      return { x: q(CX + Math.cos(a) * rad), y: q(CX + Math.sin(a) * rad), s, op: q(0.16 + r() * 0.22) }
    })
  }, [CX, R, idPrefix])

  /** Sheen de aceite: dos o tres reflejos alargados. Es lo "provocativo" —
   *  el brillo es lo que hace que la comida se vea recién hecha. */
  const brillos = useMemo(() => {
    const r = rng(`${idPrefix}-brillo`)
    return Array.from({ length: 4 }, () => {
      const a = r() * Math.PI * 2
      const rad = Math.sqrt(r()) * (R - 40)
      return {
        x: q(CX + Math.cos(a) * rad), y: q(CX + Math.sin(a) * rad),
        rx: q(10 + r() * 22), ry: q(4 + r() * 7), rot: q(r() * 180), op: q(0.08 + r() * 0.12),
      }
    })
  }, [CX, R, idPrefix])

  return (
    <svg viewBox="0 0 320 320" className={className} style={style} role="img" aria-label={title ?? "Vista de la pizza"}>
      {/* La animación se declara DENTRO del svg y con nombres derivados de
          `idPrefix`: dos pizzas en la misma página (armador + display espejado) no
          pueden compartir el nombre del keyframe o se pisan.

          Solo se anima al MONTAR. Como cada ingrediente es un <g> con clave estable
          (`optionId-zone`), React conserva los que ya estaban y monta solo el nuevo
          — así cae únicamente lo que el cajero acaba de poner, y la pizza entera no
          vuelve a llover en cada toque. */}
      {animate ? (
        <style>{`
          @keyframes ${id("k")} {
            0%   { transform: translateY(-210px); opacity: 0 }
            55%  { transform: translateY(7px);    opacity: 1 }
            78%  { transform: translateY(-3px) }
            100% { transform: translateY(0) }
          }
          .${id("cae")} { animation: ${id("k")} .54s cubic-bezier(.28,.9,.32,1) both }
          @media (prefers-reduced-motion: reduce) {
            .${id("cae")} { animation: none }
          }
        `}</style>
      ) : null}

      <defs>
        {/* Luz cálida desde arriba-izquierda, como la de un horno abierto. Todo el
            volumen del dibujo sale de que TODAS las capas comparten esa dirección. */}
        <radialGradient id={id("gMasa")} cx="38%" cy="30%">
          <stop offset="0%" stopColor="#F7CE8B" /><stop offset="55%" stopColor="#E5A855" />
          <stop offset="86%" stopColor="#C07A33" /><stop offset="100%" stopColor="#8F5424" />
        </radialGradient>
        {/* Un gradiente por tipo de salsa. Se declaran los cuatro siempre: son
            cuatro nodos, y así media pizza blanca y media roja se dibuja sin
            recalcular nada. */}
        {(["roja", "blanca", "bbq", "ninguna"] as const).map((t) => (
          <radialGradient key={t} id={id(`gS-${t}`)} cx="40%" cy="32%">
            <stop offset="0%" stopColor={COLOR_SALSA[t].claro} />
            <stop offset="100%" stopColor={COLOR_SALSA[t].oscuro} />
          </radialGradient>
        ))}

        {/* El manto de queso: dorado fundido, más tostado hacia el borde. */}
        <radialGradient id={id("gQueso")} cx="40%" cy="34%">
          <stop offset="0%" stopColor="#F7D98E" /><stop offset="62%" stopColor="#F0BE62" />
          <stop offset="100%" stopColor="#E09E42" />
        </radialGradient>

        {/* Grano orgánico. `feTurbulence` es lo que separa "formas de colores" de
            "comida": da la irregularidad que ninguna cantidad de círculos logra, y
            es determinista por `seed`, así que no rompe la hidratación. */}
        <filter id={id("fGrano")} x="0%" y="0%" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" seed="7" result="n" />
          <feColorMatrix in="n" type="saturate" values="0" />
        </filter>
        <filter id={id("fMigas")} x="0%" y="0%" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.055" numOctaves="3" seed="11" result="n" />
          <feColorMatrix in="n" type="saturate" values="0" />
        </filter>

        {/* Sombra interior donde la salsa toca el borde: es lo que hace que la
            corteza se lea LEVANTADA y no pintada. */}
        <filter id={id("fHueco")} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="4" />
        </filter>
        {/* Sombra suave: hace que los ingredientes se apoyen SOBRE la pizza en vez de
            parecer pintados dentro de ella. */}
        <filter id={id("sombra")} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="1.1" stdDeviation="1.1" floodColor="#2A1408" floodOpacity="0.42" />
        </filter>
        <clipPath id={CLIP.whole}><circle cx={CX} cy={CX} r={R} /></clipPath>
        <clipPath id={id("clipMasa")}><path d={dMasa} /></clipPath>
        <clipPath id={id("clipSalsa")}><path d={dSalsa} /></clipPath>
        <clipPath id={CLIP.left}><path d={`M${CX} ${CX - R} A${R} ${R} 0 0 0 ${CX} ${CX + R} Z`} /></clipPath>
        <clipPath id={CLIP.right}><path d={`M${CX} ${CX - R} A${R} ${R} 0 0 1 ${CX} ${CX + R} Z`} /></clipPath>
        <clipPath id={CLIP.q1}><path d={`M${CX} ${CX} L${CX} ${CX - R} A${R} ${R} 0 0 0 ${CX - R} ${CX} Z`} /></clipPath>
        <clipPath id={CLIP.q2}><path d={`M${CX} ${CX} L${CX} ${CX - R} A${R} ${R} 0 0 1 ${CX + R} ${CX} Z`} /></clipPath>
        <clipPath id={CLIP.q3}><path d={`M${CX} ${CX} L${CX + R} ${CX} A${R} ${R} 0 0 1 ${CX} ${CX + R} Z`} /></clipPath>
        <clipPath id={CLIP.q4}><path d={`M${CX} ${CX} L${CX} ${CX + R} A${R} ${R} 0 0 1 ${CX - R} ${CX} Z`} /></clipPath>
      </defs>

      {/* Todo el pastel se escala con el tamaño elegido, sobre su propio centro. Los
          recortes de zona viajan con la transformación, así que las mitades siguen
          alineadas. */}
      <g
        transform={`translate(${CX} ${CX}) scale(${escala.toFixed(3)}) translate(${-CX} ${-CX})`}
        // El tamaño se ANIMA: ver la pizza crecer al tocar "Familiar" es parte del
        // espectáculo, y un salto seco parece un error de render.
        style={{ transition: "transform .45s cubic-bezier(.25,.9,.3,1)" }}
      >
      {/* ── BASE ────────────────────────────────────────────────────────────
          En modo FOTO, la base entera (corteza + salsa + manto de queso) es UNA
          fotografía por combinación salsa×queso — no capas apiladas, porque el
          queso tapa la salsa y el anillo del borde cambia con cada combinación.
          Media roja / media BBQ = dos fotos recortadas por el clip de su zona; la
          costura queda debajo de la línea de corte que ya se dibuja encima.

          Lo que el modo foto NO puede expresar: el grosor de la masa (una sola
          corteza fotografiada). El tamaño sí — lo aplica el grupo de escala. */}
      {fotos ? (
        <>
          <ellipse
            cx={CX} cy={CX + 7} rx={R + borde - 2} ry={R + borde - 10}
            fill="#2A1408" opacity={0.24} filter={`url(#${id("fHueco")})`}
          />
          {(salsas && salsas.length > 0 ? salsas : [{ zone: "whole" as Zone, tipo: "roja" as Salsa }]).map((sa, i) => (
            <image
              key={`${sa.zone}-${i}`}
              href={fotoBase(sa.tipo, queso ?? "normal")}
              // Exactamente 2R: la foto viene recortada a su caja alfa, así que su
              // marco ES la pizza. Antes se dibujaban todas en una caja fija de 316
              // con aire propio de cada una, y el radio real saltaba entre 114 y 136
              // según la salsa — elegir BBQ agrandaba la pizza un 19 %.
              x={CX - R} y={CX - R} width={R * 2} height={R * 2}
              preserveAspectRatio="xMidYMid meet"
              clipPath={sa.zone === "whole" ? undefined : `url(#${CLIP[sa.zone]})`}
            />
          ))}
        </>
      ) : (
      <>
      {/* ── MASA ──────────────────────────────────────────────────────────── */}
      <path d={dMasa} fill="#5E3A1E" opacity={0.34} transform="translate(1,6)" filter={`url(#${id("fHueco")})`} />
      <path d={dMasa} fill={`url(#${id("gMasa")})`} />

      {/* Grano de la masa: harina y poros. Multiplicado suave sobre la corteza. */}
      <g clipPath={`url(#${id("clipMasa")})`} style={{ mixBlendMode: "multiply" }}>
        <rect x="0" y="0" width="320" height="320" filter={`url(#${id("fMigas")})`} opacity={0.16} />
      </g>

      {/* Ampollas del borde: la sombra y su brillo van juntos — una mancha plana
          se lee como suciedad, una con luz encima se lee como relieve. */}
      {char.map((c, i) => (
        <g key={i} transform={`rotate(${c.rot} ${c.x} ${c.y})`}>
          <ellipse cx={c.x} cy={c.y} rx={c.rx} ry={c.ry} fill="#6E3F1B" opacity={c.op} />
          <ellipse cx={c.x - c.rx * 0.22} cy={c.y - c.ry * 0.3} rx={c.rx * 0.6} ry={c.ry * 0.5}
                   fill="#FFE9C4" opacity={c.op * 0.42} />
        </g>
      ))}

      {/* ── SALSA ─────────────────────────────────────────────────────────── */}
      {/* El halo oscuro detrás hunde la salsa respecto del borde. */}
      <path d={dSalsa} fill="#5A1A11" opacity={0.45} filter={`url(#${id("fHueco")})`} />
      {/* Una capa por zona: media blanca y media roja se dibuja de verdad, en vez de
          elegir una y mentirle al cliente sobre la otra mitad. */}
      {(salsas && salsas.length > 0 ? salsas : [{ zone: "whole" as Zone, tipo: "roja" as Salsa }]).map((sa, i) => (
        <path key={`${sa.zone}-${i}`} d={dSalsa} fill={`url(#${id(`gS-${sa.tipo}`)})`} clipPath={`url(#${CLIP[sa.zone]})`} />
      ))}
      <g clipPath={`url(#${id("clipSalsa")})`} style={{ mixBlendMode: "multiply" }}>
        <rect x="0" y="0" width="320" height="320" filter={`url(#${id("fGrano")})`} opacity={0.13} />
      </g>

      {/* ── QUESO ─────────────────────────────────────────────────────────── */}
      {/* 🔴 EL QUESO ES UN MANTO, NO PARCHES SUELTOS. La referencia de Samuel lo
          dejó claro: en una pizza real el queso fundido CUBRE la superficie y la
          salsa solo asoma en el anillo del borde. La versión anterior era al revés
          —un campo rojo con manchas pálidas— y por eso se veía lavada por más
          textura que tuviera.

          Ligeramente translúcido (0.94): la salsa de abajo TIÑE el queso, que es lo
          que hace que media blanca / media roja se distingan también por el tono del
          manto. "Sin queso" quita la capa entera; "doble" la densifica. */}
      <g clipPath={`url(#${CLIP.whole})`} opacity={queso === "sin" ? 0 : queso === "doble" ? 1 : 0.94}>
        <path d={dQueso} fill={`url(#${id("gQueso")})`} opacity={queso === "doble" ? 0.97 : 0.88} />
        {parches.map((c, i) => <path key={i} d={c.d} fill={c.fill} opacity={c.op} />)}
        {leopardo.map((c, i) => <path key={i} d={c.d} fill={c.fill} opacity={c.op} />)}
        {burbujas.map((b, i) => (
          <g key={i}>
            <circle cx={b.x} cy={b.y} r={b.s} fill="#8A5A2A" opacity={b.op * 0.5} />
            <circle cx={b.x - b.s * 0.28} cy={b.y - b.s * 0.32} r={b.s * 0.62} fill="#FFF3D2" opacity={b.op} />
          </g>
        ))}
        {brillos.map((g, i) => (
          <ellipse key={i} cx={g.x} cy={g.y} rx={g.rx} ry={g.ry} fill="#FFFFFF" opacity={g.op}
                   transform={`rotate(${g.rot} ${g.x} ${g.y})`} />
        ))}
      </g>
      </>
      )}

      {/* Ingredientes, cada uno con su forma y recortado a su zona */}
      <g filter={`url(#${id("sombra")})`}>
        {toppings.filter((t) => !esCapaDeQueso(t.name)).map((t) => {
          const preset = presetDe(t.name)
          const forma: Forma = preset?.forma ?? "disco"
          // El color del catálogo GANA al que viene del llamador: ese venía de un
          // hash del nombre, que es lo que pintaba la piña de violeta.
          const tinte = preset?.color ?? t.color
          // Foto de la pieza si existe; `null` cae al dibujo vectorial DE ESA PIEZA
          // (no de toda la pizza): una "Chistorra de la casa" sin foto convive con
          // pepperoni fotográfico en el mismo plato.
          const foto = fotos ? fotoTopping(t.name) : null
          const r = rng(t.optionId)
          // La densidad por superficie tiene que ser la misma: media pizza lleva la
          // mitad de las piezas, no las mismas apretadas.
          const fraccion = t.zone === "whole" ? 1 : t.zone === "left" || t.zone === "right" ? 0.5 : 0.25
          // La densidad la manda el ingrediente: el orégano va en muchas escamas
          // chicas y una lonja de jamón en pocas y grandes. Un número único para
          // todos hacía que las hierbas se vieran como piedras.
          // En modo foto las piezas son más chicas que el dibujo vectorial (miden lo
          // que miden de verdad), así que con la misma cuenta la pizza se veía pelada.
          // Se compensa con más piezas, que es además lo que hace una pizzería: cubrir.
          const n = Math.max(3, Math.round((preset?.densidad ?? 15) * (foto ? 1.4 : 1) * fraccion * Math.max(1, t.quantity)))
          // 🔴 REPARTO ESTRATIFICADO, no azar puro.
          //
          // Con azar puro las piezas se apelotonan en bandas y dejan medio plato vacío:
          // con doce piezas los grumos NO se promedian, se ven. Se parte la zona en n
          // celdas de ángulo y n anillos de área, va una pieza por celda y se la sacude
          // dentro de la suya — parejo de lejos, orgánico de cerca.
          //
          // Los anillos se barajan para que ángulo y radio no queden atados: si la
          // pieza i tomara el anillo i, saldría una espiral perfecta.
          const anillos = Array.from({ length: n }, (_, i) => i)
          for (let i = n - 1; i > 0; i--) {
            const j = Math.floor(r() * (i + 1))
            ;[anillos[i], anillos[j]] = [anillos[j], anillos[i]]
          }
          return (
            <g key={`${t.optionId}-${t.zone}`} clipPath={`url(#${CLIP[t.zone]})`}>
              {Array.from({ length: n }, (_, i) => {
                const [a0, a1] = RANGO[t.zone]
                // 0.8 = cuánto puede desviarse de su celda. En 1 las celdas vecinas se
                // pisan y vuelve el apelotonamiento; en 0 queda una grilla de fábrica.
                const a = a0 + ((i + 0.5 + (r() - 0.5) * 0.8) / n) * (a1 - a0)
                // sqrt reparte parejo por ÁREA: sin él se apelotonan en el centro.
                // Lado de la pieza. En foto es una MEDIDA real (fracción del
                // diámetro); en vector sigue el multiplicador del catálogo. El ±10 %
                // evita que doce piezas idénticas se lean como un patrón estampado.
                const lado = foto
                  ? tamanoFoto(t.name) * (R * 2) * (0.9 + r() * 0.2)
                  : (4.6 + r() * 3.2) * (preset?.escala ?? 1) * 2
                const s = lado / 2
                // El ingrediente va SOBRE LA SALSA: el radio disponible descuenta la
                // corteza y además el propio tamaño de la pieza, para que ninguna
                // quede a caballo del borde ni cuelgue fuera de la pizza.
                const radioUtil = Math.max(6, (foto ? R * RADIO_SALSA : R - borde) - s * 0.8)
                // sqrt reparte parejo por ÁREA: sin él se apelotonan en el centro.
                const rad = Math.sqrt((anillos[i] + 0.5 + (r() - 0.5) * 0.8) / n) * radioUtil
                const x = CX + Math.cos(a) * rad
                const y = CX + Math.sin(a) * rad
                const rot = r() * 360
                // Envoltura SOLO para la caída. La traslación va en el <g> de
                // adentro: si la animación tocara ese mismo transform, la pieza
                // perdería su posición al terminar.
                //
                // Se anima con translateY puro, sin scale. Un scale en SVG rota
                // alrededor del origen del lienzo (0,0), no del centro de la pieza,
                // salvo que se use `transform-box: fill-box` — que no está en todos
                // los WebView de tablet. El rebote se logra con el propio recorrido
                // vertical, y así funciona en todos lados.
                return (
                  <g
                    key={i}
                    className={animate ? id("cae") : undefined}
                    style={animate ? { animationDelay: `${(i * 42) % 380}ms` } : undefined}
                  >
                    <g transform={`translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${rot.toFixed(0)})`}>
                      {foto ? (
                        <image
                          href={foto}
                          x={-s} y={-s} width={lado} height={lado}
                          preserveAspectRatio="xMidYMid meet"
                        />
                      ) : (
                        pieza(forma, tinte, s, `${t.optionId}-${i}`)
                      )}
                    </g>
                  </g>
                )
              })}
            </g>
          )
        })}
      </g>

      {/* Corte, encima de todo. Va DENTRO del grupo escalado: si no, la línea que
          separa las mitades se quedaría del largo de una familiar sobre una mediana. */}
      {layout !== "whole" ? (
        <g stroke="#2A1408" strokeOpacity={0.42} strokeWidth={2.4} strokeLinecap="round">
          <line x1={CX} y1={CX - R} x2={CX} y2={CX + R} />
          {layout === "quarters" ? <line x1={CX - R} y1={CX} x2={CX + R} y2={CX} /> : null}
        </g>
      ) : null}
      </g>
    </svg>
  )
}
